// src/pages/speaking-clubs/SpeakingClubBookings.jsx
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Loader2, Users } from "lucide-react";
import { speakingClubsAPI, flattenApiError } from "../../services/speakingClubService";

const STATUS_LABELS = {
  paid: { text: "مؤكد", cls: "bg-emerald-100 text-emerald-700" },
  pending: { text: "في الانتظار", cls: "bg-amber-100 text-amber-700" },
  failed: { text: "فشل", cls: "bg-red-100 text-red-700" },
};

export default function SpeakingClubBookings() {
  const navigate = useNavigate();
  const { clubId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setData(await speakingClubsAPI.bookings(clubId));
      } catch (err) {
        setError(flattenApiError(err.response?.data));
      } finally {
        setLoading(false);
      }
    })();
  }, [clubId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const bookings = data?.bookings || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/dashboard/speaking-clubs")}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowRight className="w-5 h-5 text-gray-600" />
        </button>
        <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
          <Users className="w-5 h-5 text-emerald-700" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">حجوزات النادي</h1>
          {data && (
            <p className="text-sm text-gray-500">
              {data.total_bookings} / {data.max_students} طالب
            </p>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm text-right">
          {error}
        </div>
      )}

      {bookings.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>لا توجد حجوزات بعد</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm text-right">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">الطالب</th>
                <th className="px-4 py-3 font-medium">البريد</th>
                <th className="px-4 py-3 font-medium">المبلغ</th>
                <th className="px-4 py-3 font-medium">الحالة</th>
                <th className="px-4 py-3 font-medium">الرقم المرجعي</th>
                <th className="px-4 py-3 font-medium">التاريخ</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => {
                const st = STATUS_LABELS[b.payment_status] || STATUS_LABELS.pending;
                return (
                  <tr key={b.id} className="border-t border-gray-100">
                    <td className="px-4 py-3 font-medium text-gray-900">{b.student_name}</td>
                    <td className="px-4 py-3 text-gray-600" dir="ltr">{b.student_email}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {Number(b.amount) > 0 ? `${b.amount} ريال` : "مجاني"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>
                        {st.text}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{b.reference_number}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {new Date(b.created_at).toLocaleDateString("ar-EG")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
